import api from '../api/config';
import type { ChatDoc } from '@odo/shared-ui';
import apiV2 from '../api/v2';
import { updatePatientCreation } from './patient/creation';

export interface PatientRedirectData {
    id: string;
    requiredAction: string | null;
}

interface PatientRedirectApiResponse {
    id: string;
    status?: { actionRequired: string | null };
}

export async function fetchPatientRedirectData(patientId: string): Promise<PatientRedirectData> {
    const response = await apiV2.get<PatientRedirectApiResponse>(
        `/patients/${encodeURIComponent(patientId)}?include=status`
    );
    const data = response.data;
    return {
        id: data.id,
        requiredAction: data.status?.actionRequired ?? null
    };
}

export interface RecordsPatientData {
    id: string;
    name: string;
    status?: { caseStatus: string | null };
}

export async function fetchRecordsPatientHeader(patientId: string): Promise<RecordsPatientData> {
    const response = await apiV2.get<RecordsPatientData>(
        `/patients/${encodeURIComponent(patientId)}?include=status`
    );
    return response.data;
}

export type { ChatMessage, ChatAsset, ChatFeed } from '@odo/shared-ui';
import type { ChatFeed } from '@odo/shared-ui';

export async function fetchPatientChat(id: string): Promise<ChatFeed> {
    const response = await api.get(`/patients/${id}/chat`);
    if (response.status !== 200) {
        throw new Error(`Failed to fetch chat for patient ${id}`);
    }
    return response.data;
}

export async function sendPatientChatMessage(
    id: string,
    message: string,
    bodyDoc?: ChatDoc
): Promise<{ updateId: number } | null> {
    const response = await api.post(`/patients/${id}/chat/message`, { message, bodyDoc });
    return response.data;
}

export async function editPatientChatMessage(
    patientId: string,
    updateId: string,
    message: string,
    bodyDoc?: ChatDoc
): Promise<{ updateId: number }> {
    const response = await api.post(`/patients/${patientId}/chat/message/${updateId}`, {
        message,
        bodyDoc
    });
    return response.data;
}

export async function fetchChatStatus(
    id: string
): Promise<{ hasUpdate: boolean; otherSideLastSeenAt: string | null }> {
    const response = await api.get(`/patients/${id}/chat-status`, {
        params: { viewerType: 'practice' }
    });
    return response.data;
}

export async function markChatAsSeen(id: string): Promise<void> {
    await api.post(`/patients/${id}/chat/seen`, {}, { params: { viewerType: 'practice' } });
}

export async function markChatAsUnread(id: string): Promise<void> {
    await api.post(`/patients/${id}/chat/unread`, {}, { params: { viewerType: 'practice' } });
}

export async function endTreatment(
    id: string,
    retainerOption: string,
    reason?: string
): Promise<void> {
    const trimmedReason = reason?.trim();
    await apiV2.post(`/patients/${encodeURIComponent(id)}/workflow/treatment/end-treatment`, {
        retainerOption,
        ...(trimmedReason ? { reason: trimmedReason } : {})
    });
}

export async function triggerRefinement(id: string): Promise<void> {
    await apiV2.post(
        `/patients/${encodeURIComponent(id)}/workflow/treatment/trigger-refinement`,
        {}
    );
}

export interface ReactivateCaseResult {
    /** The case reopened, but the mirrored revision timeline event did not write,
     *  so the Revisions UI will show no reason for the reopen. */
    conversationMirrorFailed: boolean;
}

/**
 * Reopens a terminal case back into the redesign loop. The graph offers no
 * reactivate edge from "Term - 1. Done", so a completed case is rejected by the
 * backend regardless of what the UI allows.
 */
export async function reactivateCase(id: string): Promise<ReactivateCaseResult> {
    const response = await apiV2.post(
        `/patients/${encodeURIComponent(id)}/workflow/terminal/reactivate`,
        {}
    );
    return {
        conversationMirrorFailed: response.data?.data?.conversationMirrorFailed === true
    };
}

export {
    approveRetention,
    refuseRetentionContinue,
    refuseRetentionEnd,
    submitRetentionRecords,
    deliverRetainers
} from './patient/retention';

export async function updateScanId(id: string, scanId: string): Promise<void> {
    // Reuses the v2 partial patient-edit endpoint (PATCH /patients/:id/creation);
    // passing only the scan number writes iteroScanId to the DB + Monday column
    // with no other side effects, replacing the v1 treatment-plan/stl endpoint.
    await updatePatientCreation(id, { patientIteroScanNumber: scanId });
}

export interface StartMonitoringPatient {
    id: string;
    name: string;
    email: string | null;
    // Present when fetched with the monitoring block; lets the modal offer
    // "resume" instead of "start" when monitoring is paused.
    monitoring?: { monitoringStatus: string | null } | null;
}

export async function fetchPatientForMonitoring(
    patientId: string
): Promise<StartMonitoringPatient> {
    const response = await apiV2.get<StartMonitoringPatient>(
        `/patients/${encodeURIComponent(patientId)}?include=monitoring`
    );
    return response.data;
}

export interface ReviewPatient {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    dateOfBirth: string | null;
    caseType: string | null;
    subCaseType: string | null;
    manufacturer: string | null;
    manufacturerLink: string | null;
    treatmentDuration: string | null;
    refinementNotes: string | null;
    retentionNotes: string | null;
    dmId: number | null;
    totalAligners: number | null;
    version: string | null;
}

interface ReviewPatientApiResponse {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    dateOfBirth: string | null;
    status?: {
        version: string | null;
    };
    treatment?: {
        caseType: string | null;
        subCaseType: string | null;
        manufacturer: string | null;
        manufacturerLink: string | null;
        treatmentDuration: string | null;
    };
    notes?: {
        refinementNotes: string | null;
        retentionNotes: string | null;
    };
    monitoring?: {
        dmId: number | null;
        totalAligners: number | null;
    };
}

export interface DeliverPatient {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    dateOfBirth: string | null;
    caseType: string | null;
    subCaseType: string | null;
    manufacturer: string | null;
    manufacturerLink: string | null;
    treatmentDuration: string | null;
    alignerOption: string | null;
    dmAppActivationStatus: string | null;
}

interface DeliverPatientApiResponse {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    dateOfBirth: string | null;
    treatment?: {
        caseType: string | null;
        subCaseType: string | null;
        manufacturer: string | null;
        manufacturerLink: string | null;
        treatmentDuration: string | null;
        alignerOption: string | null;
    };
    monitoring?: {
        dmAppActivationStatus: string | null;
    };
}

export async function fetchDeliverPatient(patientId: string): Promise<DeliverPatient> {
    const response = await apiV2.get<DeliverPatientApiResponse>(
        `/patients/${encodeURIComponent(patientId)}?include=treatment,monitoring`
    );
    const data = response.data;
    return {
        id: data.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        dateOfBirth: data.dateOfBirth,
        caseType: data.treatment?.caseType ?? null,
        subCaseType: data.treatment?.subCaseType ?? null,
        manufacturer: data.treatment?.manufacturer ?? null,
        manufacturerLink: data.treatment?.manufacturerLink ?? null,
        treatmentDuration: data.treatment?.treatmentDuration ?? null,
        alignerOption: data.treatment?.alignerOption ?? null,
        dmAppActivationStatus: data.monitoring?.dmAppActivationStatus ?? null
    };
}

export interface ApprovePatient {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    dateOfBirth: string | null;
    internalCaseStatus: string | null;
    caseType: string | null;
    subCaseType: string | null;
    manufacturer: string | null;
    manufacturerLink: string | null;
    treatmentDuration: string | null;
    orthopulseRecommendation: string | null;
    /** OrthoPulse list price for the patient's practice, in cents, resolved
     *  server-side from the product catalog against that practice's market.
     *  Null when the catalog has no matching version. */
    orthopulsePriceCents: number | null;
    version: string | null;
}

interface ApprovePatientApiResponse {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    dateOfBirth: string | null;
    status?: {
        internalCaseStatus: string | null;
        version: string | null;
    };
    treatment?: {
        caseType: string | null;
        subCaseType: string | null;
        manufacturer: string | null;
        manufacturerLink: string | null;
        treatmentDuration: string | null;
    };
    orthopulse?: {
        recommendation: string | null;
        price: string | null;
        priceCents: number | null;
    };
}

export async function fetchApprovePatient(patientId: string): Promise<ApprovePatient> {
    const response = await apiV2.get<ApprovePatientApiResponse>(
        `/patients/${encodeURIComponent(patientId)}?include=status,treatment,orthopulse`
    );
    const data = response.data;
    return {
        id: data.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        dateOfBirth: data.dateOfBirth,
        internalCaseStatus: data.status?.internalCaseStatus ?? null,
        caseType: data.treatment?.caseType ?? null,
        subCaseType: data.treatment?.subCaseType ?? null,
        manufacturer: data.treatment?.manufacturer ?? null,
        manufacturerLink: data.treatment?.manufacturerLink ?? null,
        treatmentDuration: data.treatment?.treatmentDuration ?? null,
        orthopulseRecommendation: data.orthopulse?.recommendation ?? null,
        orthopulsePriceCents: data.orthopulse?.priceCents ?? null,
        version: data.status?.version ?? null
    };
}

export interface RefinementApprovalPatient {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    dateOfBirth: string | null;
    internalCaseStatus: string | null;
    caseType: string | null;
    subCaseType: string | null;
    manufacturer: string | null;
    manufacturerLink: string | null;
    treatmentDuration: string | null;
    version: string | null;
}

interface RefinementApprovalPatientApiResponse {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    dateOfBirth: string | null;
    status?: {
        internalCaseStatus: string | null;
        version: string | null;
    };
    treatment?: {
        caseType: string | null;
        subCaseType: string | null;
        manufacturer: string | null;
        manufacturerLink: string | null;
        treatmentDuration: string | null;
    };
}

export async function fetchRefinementApprovalPatient(
    patientId: string
): Promise<RefinementApprovalPatient> {
    const response = await apiV2.get<RefinementApprovalPatientApiResponse>(
        `/patients/${encodeURIComponent(patientId)}?include=status,treatment`
    );
    const data = response.data;
    return {
        id: data.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        dateOfBirth: data.dateOfBirth,
        internalCaseStatus: data.status?.internalCaseStatus ?? null,
        caseType: data.treatment?.caseType ?? null,
        subCaseType: data.treatment?.subCaseType ?? null,
        manufacturer: data.treatment?.manufacturer ?? null,
        manufacturerLink: data.treatment?.manufacturerLink ?? null,
        treatmentDuration: data.treatment?.treatmentDuration ?? null,
        version: data.status?.version ?? null
    };
}

export interface OverviewPatient {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    dateOfBirth: string | null;
    // status block
    caseStatus: string | null;
    actionRequired: string | null;
    /** Backs client-side action gating in actionAvailability.ts (Actions tab). */
    internalCaseStatus: string | null;
    version: string | null;
    // treatment block
    caseType: string | null;
    subCaseType: string | null;
    manufacturer: string | null;
    manufacturerLink: string | null;
    treatmentDuration: string | null;
    // monitoring block
    monitoringStatus: string | null;
    currentAligner: number | null;
    totalAligners: number | null;
    dmAppActivationStatus: string | null;
    dmLink: string | null;
    dmId: number | null;
    // orthopulse block
    orthopulseStatus: string | null;
}

interface OverviewPatientApiResponse {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    dateOfBirth: string | null;
    status?: {
        caseStatus: string | null;
        actionRequired: string | null;
        internalCaseStatus: string | null;
        version: string | null;
    };
    treatment?: {
        caseType: string | null;
        subCaseType: string | null;
        manufacturer: string | null;
        manufacturerLink: string | null;
        treatmentDuration: string | null;
    };
    monitoring?: {
        monitoringStatus: string | null;
        currentAligner: number | null;
        totalAligners: number | null;
        dmAppActivationStatus: string | null;
        dmLink: string | null;
        dmId: number | null;
    };
    orthopulse?: {
        status: string | null;
    };
}

export async function fetchOverviewPatient(patientId: string): Promise<OverviewPatient> {
    const response = await apiV2.get<OverviewPatientApiResponse>(
        `/patients/${encodeURIComponent(patientId)}?include=status,treatment,monitoring,orthopulse`
    );
    const data = response.data;
    return {
        id: data.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        dateOfBirth: data.dateOfBirth,
        caseStatus: data.status?.caseStatus ?? null,
        actionRequired: data.status?.actionRequired ?? null,
        internalCaseStatus: data.status?.internalCaseStatus ?? null,
        version: data.status?.version ?? null,
        caseType: data.treatment?.caseType ?? null,
        subCaseType: data.treatment?.subCaseType ?? null,
        manufacturer: data.treatment?.manufacturer ?? null,
        manufacturerLink: data.treatment?.manufacturerLink ?? null,
        treatmentDuration: data.treatment?.treatmentDuration ?? null,
        monitoringStatus: data.monitoring?.monitoringStatus ?? null,
        currentAligner: data.monitoring?.currentAligner ?? null,
        totalAligners: data.monitoring?.totalAligners ?? null,
        dmAppActivationStatus: data.monitoring?.dmAppActivationStatus ?? null,
        dmLink: data.monitoring?.dmLink ?? null,
        dmId: data.monitoring?.dmId ?? null,
        orthopulseStatus: data.orthopulse?.status ?? null
    };
}

export interface DashboardPatient {
    id: string;
    name: string;
    email: string | null;
    startDate: string | null;
    practiceIds: string[];
    dmLink: string | null;
    treatmentStatus: string | null;
    alignerNumber: number | null;
    alignerTotal: number | null;
    requiredAction: string | null;
    /**
     * The internal workflow status id (e.g. 'Hold - 2. In-office step
     * needed') - distinct from `treatmentStatus` (the practice-facing case
     * status) and from `requiredAction` (which can be ambiguous: the same
     * external action label sometimes covers more than one internal
     * status). See `INTERNAL_STATUS_HOLD_IN_OFFICE` in utils/constants.ts.
     */
    internalCaseStatus: string | null;
    manufacturerLink: string | null;
    manufacturer: string | null;
    monitoringStatus: string | null;
    /** ISO timestamp; null when there's no active case-design credit or the case is already approved. */
    approvalCreditExpiresAt: string | null;
    /**
     * Patient flow version - 'V1' | 'V2' raw from Monday, or null when unset.
     * Stored casing isn't normalized on write outside patient creation, so
     * compare defensively rather than by strict equality.
     */
    version: string | null;
}

interface DashboardPatientApiResponse {
    id: string;
    name: string;
    email: string | null;
    practiceId: string | null;
    scheduledStartDate: string | null;
    caseStatus: string | null;
    actionRequired: string | null;
    // Optional: guards against calling this before the backend adds
    // internal_case_status to listPatientsForPractices's projection.
    internalCaseStatus?: string | null;
    currentAligner: number | null;
    totalAligners: number | null;
    manufacturerLink: string | null;
    dmLink: string | null;
    manufacturer: string | null;
    monitoringStatus: string | null;
    approvalCreditExpiresAt: string | null;
    // Optional for the same reason as internalCaseStatus above: tolerates a
    // frontend deploy that lands before the backend projects md.version.
    version?: string | null;
}

/**
 * Practice-scoped patient list for the practitioner dashboard, served by the v2
 * GET /patients/mine endpoint (scoped server-side to the caller's practices).
 * The whole set is returned; the dashboard filters/sorts/paginates client-side.
 */
export async function fetchDashboardPatients(): Promise<DashboardPatient[]> {
    const response = await apiV2.get<DashboardPatientApiResponse[]>('/patients/mine');
    return response.data.map((p) => ({
        id: p.id,
        name: p.name,
        email: p.email,
        startDate: p.scheduledStartDate,
        practiceIds: p.practiceId ? [p.practiceId] : [],
        dmLink: p.dmLink,
        treatmentStatus: p.caseStatus,
        alignerNumber: p.currentAligner,
        alignerTotal: p.totalAligners,
        requiredAction: p.actionRequired,
        internalCaseStatus: p.internalCaseStatus ?? null,
        manufacturerLink: p.manufacturerLink,
        manufacturer: p.manufacturer,
        monitoringStatus: p.monitoringStatus,
        approvalCreditExpiresAt: p.approvalCreditExpiresAt ?? null,
        version: p.version ?? null
    }));
}

export async function fetchReviewPatient(patientId: string): Promise<ReviewPatient> {
    const response = await apiV2.get<ReviewPatientApiResponse>(
        `/patients/${encodeURIComponent(patientId)}?include=status,treatment,notes,monitoring`
    );
    const data = response.data;
    return {
        id: data.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        dateOfBirth: data.dateOfBirth,
        caseType: data.treatment?.caseType ?? null,
        subCaseType: data.treatment?.subCaseType ?? null,
        manufacturer: data.treatment?.manufacturer ?? null,
        manufacturerLink: data.treatment?.manufacturerLink ?? null,
        treatmentDuration: data.treatment?.treatmentDuration ?? null,
        refinementNotes: data.notes?.refinementNotes ?? null,
        retentionNotes: data.notes?.retentionNotes ?? null,
        dmId: data.monitoring?.dmId ?? null,
        totalAligners: data.monitoring?.totalAligners ?? null,
        version: data.status?.version ?? null
    };
}
